import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useLocation } from "react-router-dom";
import api from "../api";
import PageHeader from "./PageHeader";

const levels = [0, 1, 2, 3, 4, 5];
const resolveCollectionValue = (items = [], candidate = "") => {
  if (!items.length) return "";
  if (!candidate) return items[0]._id;

  const rawCandidate = String(candidate);
  let decodedCandidate = rawCandidate;
  try {
    decodedCandidate = decodeURIComponent(rawCandidate);
  } catch (error) {
    decodedCandidate = rawCandidate;
  }
  const normalizedValues = [rawCandidate, decodedCandidate].map((value) => value.toLowerCase());

  const match = items.find((item) =>
    [String(item._id || ""), String(item.nom || "")]
      .map((value) => value.toLowerCase())
      .some((value) => normalizedValues.includes(value))
  );

  return match?._id || items[0]._id;
};

const getSummaryFromCompetences = (competences = [], keepValidated = false, previousSummary = {}) => {
  const competencesTotal = competences.length;
  const competencesEvaluees = competences.filter((item) => item.isEvaluated).length;
  const totalValeur = competences.reduce((sum, item) => sum + Number(item.valeur || 0), 0);
  const totalPoints = competences.reduce((sum, item) => sum + Number(item.pointsObtenus || 0), 0);
  const isComplete = competencesTotal > 0 && competencesEvaluees === competencesTotal;
  const computedNoteSur20 = totalValeur > 0 ? Math.round(((totalPoints / totalValeur) * 20) * 100) / 100 : null;

  return {
    ...previousSummary,
    competencesTotal,
    competencesEvaluees,
    totalValeur,
    totalPoints,
    isComplete,
    computedNoteSur20,
    noteSur20: isComplete
      ? (keepValidated && previousSummary.noteValidated && previousSummary.noteSur20 !== null
        ? previousSummary.noteSur20
        : computedNoteSur20)
      : null,
    noteValidated: keepValidated ? !!previousSummary.noteValidated : false,
    validatedAt: keepValidated ? previousSummary.validatedAt || null : null,
  };
};

export default function SuiviElevesDashboard() {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [classes, setClasses] = useState([]);
  const [activites, setActivites] = useState([]);
  const [currentClasseId, setCurrentClasseId] = useState(query.get('classe') || "");
  const [currentActiviteId, setCurrentActiviteId] = useState(query.get('activite') || "");
  const [competences, setCompetences] = useState([]);
  const [elevesData, setElevesData] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [competenceDraftValues, setCompetenceDraftValues] = useState({});
  const pendingSaveTimeoutsRef = useRef({});
  const pendingSaveVersionsRef = useRef({});
  const pendingLevelSavePayloadsRef = useRef({});
  const pendingCompetenceSaveTimeoutsRef = useRef({});

  useEffect(() => () => {
    Object.values(pendingSaveTimeoutsRef.current).forEach((timerId) => clearTimeout(timerId));
    Object.values(pendingCompetenceSaveTimeoutsRef.current).forEach((timerId) => clearTimeout(timerId));
    pendingSaveTimeoutsRef.current = {};
    pendingLevelSavePayloadsRef.current = {};
    pendingCompetenceSaveTimeoutsRef.current = {};
  }, []);

  const updateEleveEntry = (eleveId, updater) => {
    setElevesData((prev) => {
      const index = prev.findIndex((entry) => entry.eleve._id === eleveId);
      if (index === -1) return prev;
      const currentEntry = prev[index];
      const nextEntry = updater(currentEntry);
      if (nextEntry === currentEntry) return prev;
      const next = [...prev];
      next[index] = nextEntry;
      return next;
    });
  };

  useEffect(() => {
    async function loadFilters() {
      setLoadingFilters(true);
      try {
        const [classesRes, activitesRes] = await Promise.all([
          api.get('/classes'),
          api.get('/activites')
        ]);
        const loadedClasses = Array.isArray(classesRes.data) ? classesRes.data : [];
        const loadedActivites = Array.isArray(activitesRes.data) ? activitesRes.data : [];
        setClasses(loadedClasses);
        setActivites(loadedActivites);
        setCurrentClasseId((prev) => resolveCollectionValue(loadedClasses, query.get('classe') || prev));
        setCurrentActiviteId((prev) => resolveCollectionValue(loadedActivites, query.get('activite') || prev));
      } catch (err) {
        setSnack({ open: true, message: err.response?.data?.error || "Erreur lors du chargement des filtres.", severity: "error" });
      } finally {
        setLoadingFilters(false);
      }
    }

    loadFilters();
  }, [query]);

  useEffect(() => {
    if (!currentClasseId || !currentActiviteId) return;

    async function loadDashboard() {
      setLoadingData(true);
      try {
        const res = await api.get(`/elevesuivi/classe/${currentClasseId}/${currentActiviteId}`);
        setCompetences(Array.isArray(res.data?.competences) ? res.data.competences : []);
        setCompetenceDraftValues({});
        setElevesData(Array.isArray(res.data?.eleves) ? res.data.eleves : []);
      } catch (err) {
        if (!err?.response || err?.response?.status === 404 || err?.response?.status >= 500) {
          try {
            const [elevesRes, competencesRes] = await Promise.all([
              api.get(`/eleves?classe=${encodeURIComponent(currentClasseId)}`),
              api.get(`/competences/${currentActiviteId}`)
            ]);

            const loadedEleves = Array.isArray(elevesRes.data) ? elevesRes.data : [];
            const loadedCompetences = Array.isArray(competencesRes.data)
              ? competencesRes.data.map((competence) => ({
                  ...competence,
                  valeur: Number.isFinite(Number(competence.valeur)) ? Number(competence.valeur) : 1
                }))
              : [];

            const suivis = await Promise.all(
              loadedEleves.map(async (eleve) => {
                const suiviRes = await api.get(`/elevesuivi/${eleve._id}/${currentActiviteId}`);
                const suiviById = new Map(
                  (Array.isArray(suiviRes.data) ? suiviRes.data : []).map((competence) => [competence._id, competence])
                );

                const mergedCompetences = loadedCompetences.map((competence) => {
                  const suivi = suiviById.get(competence._id);
                  const niveau = Number(suivi?.niveau || 0);
                  const valeur = Number(competence.valeur || 1);
                  return {
                    ...competence,
                    niveau,
                    isEvaluated: !!suivi,
                    pointsObtenus: Math.round(((valeur * niveau) / 5) * 100) / 100,
                    noteValidated: !!suivi?.noteValidated,
                    noteSur20: suivi?.noteSur20 ?? null,
                    validatedAt: suivi?.validatedAt || null,
                  };
                });

                return {
                  eleve,
                  competences: mergedCompetences,
                  summary: getSummaryFromCompetences(
                    mergedCompetences,
                    mergedCompetences.some((competence) => competence.noteValidated),
                    {
                      noteValidated: mergedCompetences.some((competence) => competence.noteValidated),
                      noteSur20: mergedCompetences.find((competence) => competence.noteValidated)?.noteSur20 ?? null,
                      validatedAt: mergedCompetences.find((competence) => competence.noteValidated)?.validatedAt || null,
                    }
                  ),
                };
              })
            );

            setCompetences(loadedCompetences);
            setCompetenceDraftValues({});
            setElevesData(suivis);
            setSnack({
              open: true,
              message: "Le suivi a été chargé via le mode de compatibilité.",
              severity: "info"
            });
          } catch (fallbackErr) {
            setSnack({
              open: true,
              message: fallbackErr.response?.data?.error || "Erreur lors du chargement du suivi.",
              severity: "error"
            });
            setCompetences([]);
            setElevesData([]);
          }
        } else {
          setSnack({ open: true, message: err.response?.data?.error || "Erreur lors du chargement du suivi.", severity: "error" });
          setCompetences([]);
          setElevesData([]);
        }
      } finally {
        setLoadingData(false);
      }
    }

    loadDashboard();
  }, [currentClasseId, currentActiviteId]);

  const handleSaveValeur = async (competenceId, valeur, options = {}) => {
    const { silent = false } = options;
    const parsedValeur = Number(valeur);
    if (!Number.isFinite(parsedValeur) || parsedValeur < 0) {
      if (!silent) {
        setSnack({ open: true, message: "La valeur doit être un nombre positif.", severity: "error" });
      }
      return false;
    }

    setSavingKey(`valeur-${competenceId}`);
    try {
      const res = await api.put(`/competences/${competenceId}`, { valeur: parsedValeur });
      const savedValeur = Number.isFinite(Number(res.data?.valeur)) ? Number(res.data.valeur) : parsedValeur;
      const evaluationsReset = !!res.data?.evaluationsReset;
      setCompetences((prev) => prev.map((comp) => comp._id === competenceId ? { ...comp, valeur: savedValeur } : comp));
      setElevesData((prev) => prev.map((entry) => {
        const nextCompetences = entry.competences.map((comp) => comp._id === competenceId
          ? {
            ...comp,
            valeur: savedValeur,
            noteValidated: evaluationsReset ? false : comp.noteValidated,
            noteSur20: evaluationsReset ? null : comp.noteSur20,
            validatedAt: evaluationsReset ? null : comp.validatedAt,
            pointsObtenus: Math.round(((savedValeur * (comp.niveau || 0)) / 5) * 100) / 100
          }
          : comp);
        return {
          ...entry,
          competences: nextCompetences,
          summary: getSummaryFromCompetences(nextCompetences, !evaluationsReset, evaluationsReset ? {} : entry.summary),
        };
      }));
      setCompetenceDraftValues((prev) => {
        const next = { ...prev };
        delete next[competenceId];
        return next;
      });
      if (!silent) {
        setSnack({
          open: true,
          message: evaluationsReset ? "Valeur mise à jour. Les notes validées ont été réinitialisées." : "Valeur de compétence mise à jour.",
          severity: "success"
        });
      }
      return true;
    } catch (err) {
      setCompetenceDraftValues((prev) => ({
        ...prev,
        [competenceId]: competences.find((comp) => comp._id === competenceId)?.valeur ?? 1,
      }));
      setSnack({ open: true, message: err.response?.data?.error || "Erreur lors de la mise à jour du barème.", severity: "error" });
      return false;
    } finally {
      setSavingKey("");
    }
  };

  const handleDraftValeurChange = (competenceId, valeur) => {
    setCompetenceDraftValues((prev) => ({ ...prev, [competenceId]: valeur }));

    if (pendingCompetenceSaveTimeoutsRef.current[competenceId]) {
      clearTimeout(pendingCompetenceSaveTimeoutsRef.current[competenceId]);
    }

    pendingCompetenceSaveTimeoutsRef.current[competenceId] = setTimeout(() => {
      handleDraftValeurCommit(competenceId);
    }, 700);
  };

  const handleDraftValeurCommit = async (competenceId) => {
    if (pendingCompetenceSaveTimeoutsRef.current[competenceId]) {
      clearTimeout(pendingCompetenceSaveTimeoutsRef.current[competenceId]);
      delete pendingCompetenceSaveTimeoutsRef.current[competenceId];
    }

    const competence = competences.find((item) => item._id === competenceId);
    if (!competence) return;

    const draftValue = competenceDraftValues[competenceId];
    if (draftValue === undefined || Number(draftValue) === Number(competence.valeur ?? 1)) {
      return;
    }

    await handleSaveValeur(competenceId, draftValue);
  };

  const persistDirtyCompetenceValues = async () => {
    for (const competence of competences) {
      const draftValue = competenceDraftValues[competence._id];
      if (draftValue === undefined || Number(draftValue) === Number(competence.valeur ?? 1)) {
        continue;
      }

      const saved = await handleSaveValeur(competence._id, draftValue, { silent: true });
      if (!saved) {
        return false;
      }
    }

    return true;
  };

  const persistPendingLevelSave = async (pendingKey, payload) => {
    if (!payload) {
      return true;
    }
    const { eleveId, competenceId, niveau, previousEntry, nextVersion } = payload;

    try {
      await api.post('/elevesuivi/set', {
        eleve: eleveId,
        competence: competenceId,
        niveau,
      });
      return true;
    } catch (err) {
      if (previousEntry && pendingSaveVersionsRef.current[pendingKey] === nextVersion) {
        updateEleveEntry(eleveId, () => previousEntry);
      }
      setSnack({ open: true, message: err.response?.data?.error || "Erreur lors de la sauvegarde du niveau.", severity: "error" });
      return false;
    } finally {
      if (pendingSaveVersionsRef.current[pendingKey] === nextVersion) {
        delete pendingSaveTimeoutsRef.current[pendingKey];
        delete pendingSaveVersionsRef.current[pendingKey];
        delete pendingLevelSavePayloadsRef.current[pendingKey];
      }
    }
  };

  const flushPendingLevelSaves = async (eleveId) => {
    const pendingEntries = Object.entries(pendingLevelSavePayloadsRef.current).filter(([, payload]) => payload.eleveId === eleveId);

    for (const [pendingKey, payload] of pendingEntries) {
      if (pendingSaveTimeoutsRef.current[pendingKey]) {
        clearTimeout(pendingSaveTimeoutsRef.current[pendingKey]);
      }

      const saved = await persistPendingLevelSave(pendingKey, payload);
      if (!saved) {
        return false;
      }
    }

    return true;
  };

  const handleSetNiveau = async (eleveId, competenceId, niveau) => {
    const pendingKey = `${eleveId}-${competenceId}`;
    const previousEntry = elevesData.find((entry) => entry.eleve._id === eleveId);
    const nextVersion = (pendingSaveVersionsRef.current[pendingKey] || 0) + 1;
    pendingSaveVersionsRef.current[pendingKey] = nextVersion;

    updateEleveEntry(eleveId, (entry) => {
      const nextCompetences = entry.competences.map((comp) => comp._id === competenceId
        ? {
          ...comp,
          niveau,
          isEvaluated: true,
          noteValidated: false,
          noteSur20: null,
          validatedAt: null,
          pointsObtenus: Math.round((((Number(comp.valeur || 0)) * niveau) / 5) * 100) / 100
        }
        : comp);

      return {
        ...entry,
        competences: nextCompetences,
        summary: getSummaryFromCompetences(nextCompetences, false, entry.summary),
      };
    });

    if (pendingSaveTimeoutsRef.current[pendingKey]) {
      clearTimeout(pendingSaveTimeoutsRef.current[pendingKey]);
    }

    pendingLevelSavePayloadsRef.current[pendingKey] = {
      eleveId,
      competenceId,
      niveau,
      previousEntry,
      nextVersion,
    };

    pendingSaveTimeoutsRef.current[pendingKey] = setTimeout(() => {
      persistPendingLevelSave(pendingKey, pendingLevelSavePayloadsRef.current[pendingKey]);
    }, 180);
  };

  const handleValidateNote = async (eleveId, validated) => {
    const resolvedActiviteId = resolveCollectionValue(activites, currentActiviteId);
    if (!eleveId || !resolvedActiviteId) {
      setSnack({ open: true, message: "Activité ou élève manquant pour valider la note.", severity: "error" });
      return;
    }

    const savedPendingLevels = await flushPendingLevelSaves(eleveId);
    if (!savedPendingLevels) {
      setSnack({ open: true, message: "Les dernières évaluations doivent être enregistrées avant la validation.", severity: "error" });
      return;
    }

    const savedAllCompetenceValues = await persistDirtyCompetenceValues();
    if (!savedAllCompetenceValues) {
      setSnack({ open: true, message: "Enregistre d'abord les valeurs des compétences en erreur.", severity: "error" });
      return;
    }

    setSavingKey(`validation-${eleveId}`);
    try {
      let res;
      try {
        res = await api.post('/elevesuivi/validate-note', {
          eleve: eleveId,
          activite: resolvedActiviteId,
          validated,
        });
      } catch (err) {
        if (!err?.response || err?.response?.status === 404 || err?.response?.status >= 500) {
          res = await api.post('/elevesuivi/set', {
            eleve: eleveId,
            activite: resolvedActiviteId,
            validated,
          });
        } else {
          throw err;
        }
      }

      updateEleveEntry(eleveId, (entry) => ({
        ...entry,
        competences: entry.competences.map((competence) => ({
          ...competence,
          noteValidated: !!res.data.validated,
          noteSur20: res.data.noteSur20 ?? entry.summary.computedNoteSur20 ?? null,
          validatedAt: res.data.validatedAt || null,
        })),
        summary: {
          ...entry.summary,
          noteValidated: !!res.data.validated,
          noteSur20: res.data.noteSur20 ?? entry.summary.computedNoteSur20 ?? null,
          validatedAt: res.data.validatedAt || null,
        }
      }));
      setSnack({ open: true, message: validated ? "Note validée pour l'élève." : "Validation retirée.", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || "Erreur lors de la validation de la note.", severity: "error" });
    } finally {
      setSavingKey("");
    }
  };

  if (loadingFilters) {
    return <Box sx={{ mt: 6, textAlign: "center" }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 1280, mx: "auto" }}>
      <PageHeader
        title="Suivi des élèves"
        subtitle="Un suivi mobile centré sur l’évaluation tactile, la note /20 automatique et la validation prof."
        backTo="/prof"
        homeTo="/"
      />

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="classe-select-label">Classe</InputLabel>
              <Select
                labelId="classe-select-label"
                label="Classe"
                value={currentClasseId}
                onChange={(e) => setCurrentClasseId(e.target.value)}
              >
                {classes.map((classe) => (
                  <MenuItem key={classe._id} value={classe._id}>{classe.nom}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="activite-select-label">Activité</InputLabel>
              <Select
                labelId="activite-select-label"
                label="Activité"
                value={currentActiviteId}
                onChange={(e) => setCurrentActiviteId(e.target.value)}
              >
                {activites.map((activite) => (
                  <MenuItem key={activite._id} value={activite._id}>{activite.nom}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {!!competences.length && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
            Barème des compétences
          </Typography>
          <Grid container spacing={2}>
            {competences.map((competence) => (
              <Grid item xs={12} sm={6} lg={4} key={competence._id}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Stack spacing={1.5}>
                    <Typography sx={{ fontWeight: 700 }}>{competence.nom}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Valeur utilisée pour calculer automatiquement la note /20.
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <TextField
                        label="Valeur"
                        type="number"
                        size="small"
                        value={competenceDraftValues[competence._id] ?? competence.valeur ?? 1}
                        onChange={(e) => {
                          handleDraftValeurChange(competence._id, e.target.value);
                        }}
                        onBlur={() => handleDraftValeurCommit(competence._id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleDraftValeurCommit(competence._id);
                          }
                        }}
                        inputProps={{ min: 0, step: 0.5 }}
                        sx={{ maxWidth: 120 }}
                      />
                      <Button
                        variant="contained"
                        onClick={() => handleSaveValeur(competence._id, competenceDraftValues[competence._id] ?? competence.valeur)}
                        disabled={savingKey === `valeur-${competence._id}`}
                      >
                        Enregistrer
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {loadingData ? (
        <Box sx={{ textAlign: "center", mt: 4 }}><CircularProgress /></Box>
      ) : (!currentClasseId || !currentActiviteId || elevesData.length === 0 || competences.length === 0) ? (
        <Typography sx={{ mt: 4, color: "text.secondary" }}>
          Sélectionne une classe et une activité pour afficher les évaluations.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {elevesData.map((entry) => {
            const noteLabel = entry.summary.isComplete ? `${entry.summary.noteSur20 ?? entry.summary.computedNoteSur20}/20` : "À compléter";

            return (
              <Accordion key={entry.eleve._id} disableGutters sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    sx={{ width: '100%' }}
                  >
                    <Typography sx={{ fontWeight: 800, flex: 1 }}>
                      {entry.eleve.prenom} {entry.eleve.nom}
                    </Typography>
                    <Chip label={noteLabel} color={entry.summary.isComplete ? "primary" : "default"} />
                    <Chip
                      label={entry.summary.noteValidated ? "Note validée" : "Brouillon prof"}
                      color={entry.summary.noteValidated ? "success" : "warning"}
                      variant={entry.summary.noteValidated ? "filled" : "outlined"}
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1.5}
                      alignItems={{ xs: 'stretch', md: 'center' }}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>
                          {entry.summary.competencesEvaluees}/{entry.summary.competencesTotal} compétences évaluées
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total acquis : {entry.summary.totalPoints?.toFixed?.(2) ?? entry.summary.totalPoints} / {entry.summary.totalValeur?.toFixed?.(2) ?? entry.summary.totalValeur}
                        </Typography>
                      </Box>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          variant="outlined"
                          endIcon={<OpenInNewRoundedIcon />}
                          onClick={() => window.open(`/eleve/${entry.eleve._id}?activity=${encodeURIComponent(currentActiviteId)}`, '_blank', 'noopener,noreferrer')}
                        >
                          Voir l’espace élève
                        </Button>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={!!entry.summary.noteValidated}
                              disabled={!entry.summary.isComplete || savingKey === `validation-${entry.eleve._id}`}
                              onChange={(e) => handleValidateNote(entry.eleve._id, e.target.checked)}
                            />
                          )}
                          label="Note validée"
                        />
                      </Stack>
                    </Stack>

                    {!entry.summary.isComplete && (
                      <Alert severity="info">
                        La note /20 sera calculée automatiquement dès que toutes les compétences auront une évaluation.
                      </Alert>
                    )}

                    <Grid container spacing={1.5}>
                      {entry.competences.map((competence) => (
                        <Grid item xs={12} key={competence._id}>
                          <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Stack spacing={1}>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                justifyContent="space-between"
                              >
                                <Box>
                                  <Typography sx={{ fontWeight: 700 }}>{competence.nom}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {competence.pointsObtenus} / {competence.valeur} points
                                  </Typography>
                                </Box>
                                <Chip
                                  label={competence.isEvaluated ? `${competence.niveau}/5` : 'Non évaluée'}
                                  color={competence.isEvaluated ? "primary" : "default"}
                                  variant={competence.isEvaluated ? "filled" : "outlined"}
                                />
                              </Stack>

                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                {levels.map((level) => (
                                  <IconButton
                                    key={level}
                                    onClick={() => handleSetNiveau(entry.eleve._id, competence._id, level)}
                                    color={level === 0 ? "default" : "warning"}
                                    aria-label={level === 0
                                      ? `Remettre la compétence ${competence.nom} à 0 sur 5 pour ${entry.eleve.prenom} ${entry.eleve.nom}`
                                      : `Mettre la compétence ${competence.nom} à ${level} sur 5 pour ${entry.eleve.prenom} ${entry.eleve.nom}`}
                                    sx={{
                                      width: 42,
                                      height: 42,
                                      border: level === competence.niveau ? '2px solid currentColor' : '1px solid rgba(0,0,0,0.08)'
                                    }}
                                  >
                                    {level === 0 ? (
                                      <Typography variant="caption" sx={{ fontWeight: 700 }}>0</Typography>
                                    ) : level <= competence.niveau ? (
                                      <StarRoundedIcon />
                                    ) : (
                                      <StarBorderRoundedIcon />
                                    )}
                                  </IconButton>
                                ))}
                              </Stack>
                            </Stack>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
